import type {SeriesOptionsType} from 'highcharts';
import {countHtml, escapeHtml, formatValue, previousHtml} from '../render/format';
import {isLowBase, lowBaseAttributes} from '../render/lowBase';
import {MarkRenderer} from '../types';
import {buildHierarchy, HierarchyNode, nodeTooltip, sizeDetailsHtml} from './hierarchy';

/**
 * Dims every node outside the drill group of the hovered leaf. Hover targets leaves (interactByLeaf), so without this
 * nothing shows which leaves a click would open together.
 */
function highlightGroup(series: any, groupId: string | false | undefined): void {
    series.points.forEach((point: any) => {
        let node = point.node;
        while (groupId && node && node.id !== groupId) {
            node = series.nodeMap[node.parent];
        }
        const opacity = !groupId || node ? 1 : 0.35;
        point.graphic?.attr({opacity});
        point.dataLabel?.attr({opacity});
    });
}

const renderTreemap: MarkRenderer = (cube, spec, ctx) => {
    const hierarchy = buildHierarchy(cube, spec, ctx);
    const {sizeMeasure, colorMeasure} = hierarchy;
    const levelCount = hierarchy.dimensionIds.length;
    const labels = spec.options.dataLabels !== false;
    const innerColor = ctx.dark ? '#2B2B2B' : '#E9ECEF';

    // Leaves only show the deepest member, so the tooltip names every level and both measures
    const tooltip = (node: HierarchyNode, lowBase: boolean): string => {
        const head = nodeTooltip(node, hierarchy, ctx, !colorMeasure);
        if (!node.leaf) {
            return head;
        }
        if (!colorMeasure) {
            return head + sizeDetailsHtml(node, sizeMeasure, lowBase, ctx);
        }
        const color = node.leaf.color;

        return `${head}<br>${escapeHtml(colorMeasure.label)}: <b>${formatValue(color?.value, colorMeasure, ctx)}</b>, `
            + countHtml(color, lowBase) + previousHtml(color, colorMeasure, ctx);
    };

    const data = hierarchy.nodes.map(node => {
        const leaf = node.leaf;
        // The colour value is what the reader judges, so its base decides the low-base marking
        const lowBase = leaf ? isLowBase(cube, colorMeasure ? leaf.color : leaf.sizeCell) : false;

        const point: Record<string, any> = {
            id: node.id,
            name: escapeHtml(node.label),
            custom: {lowBase, tooltip: tooltip(node, lowBase)},
        };
        if (node.parentId) {
            point.parent = node.parentId;
        }
        if (leaf) {
            point.value = leaf.size;
            // Point options win over level options, so a leaf keeps its centred label even when it is a top group
            point.dataLabels = {align: 'center', verticalAlign: 'middle', style: {fontSize: '12px', fontWeight: 'normal'}};
        }
        if (colorMeasure) {
            // Inner nodes are covered by their children; they only need a neutral fill
            if (leaf) {
                point.colorValue = leaf.color?.value ?? null;
            } else {
                point.color = innerColor;
            }
        } else {
            point.color = node.rootColor;
        }

        return {...point, ...(lowBase ? lowBaseAttributes('pie', node.rootColor, ctx) : {})};
    });

    const values = data.map(point => point.colorValue).filter((value): value is number => typeof value === 'number');
    const scale = colorMeasure?.scale;
    const border = ctx.dark ? '#1E1E1E' : '#FFFFFF';

    // Levels count from the current drill root (levelIsConstant: false), so the top visible groups always get the
    // headers; deeper inner levels stay unlabelled because Highcharts 9 has no header space and they would collide
    const levels: Record<string, any>[] = [];
    for (let level = 1; level <= levelCount; level++) {
        levels.push({
            level,
            borderColor: border,
            borderWidth: level === levelCount ? 1 : levelCount - level + 2,
            layoutAlgorithm: 'squarified',
            ...(level === 1 && levelCount > 1 ? {dataLabels: {enabled: labels, align: 'left', verticalAlign: 'top', style: {fontSize: '13px', fontWeight: 'bold'}}} : {}),
        });
    }

    return {
        type: 'highcharts',
        options: {
            chart: {type: 'treemap'},
            title: {text: undefined},
            credits: {enabled: false},
            legend: {enabled: colorMeasure !== undefined, title: {text: colorMeasure ? escapeHtml(colorMeasure.label) : undefined}},
            tooltip: {headerFormat: '', pointFormat: '{point.custom.tooltip}'},
            ...(colorMeasure ? {
                colorAxis: {
                    min: scale?.min ?? (values.length ? Math.min(...values) : 0),
                    max: scale?.max ?? (values.length ? Math.max(...values) : 1),
                    minColor: ctx.dark ? '#2B2B2B' : '#FFFFFF',
                    maxColor: colorMeasure.color ?? ctx.palette[0],
                    labels: {overflow: 'allow', formatter(this: any) {
                        return formatValue(this.value, colorMeasure, ctx);
                    }},
                },
            } : {}),
            plotOptions: {
                series: {dataLabels: {enabled: labels, format: '{point.name}', style: {textOutline: 'none'}}},
            },
            series: [{
                type: 'treemap',
                id: sizeMeasure.id,
                name: escapeHtml(sizeMeasure.label),
                data,
                layoutAlgorithm: 'squarified',
                allowTraversingTree: levelCount > 1,
                // Hover and click target the leaf; a click drills into its top visible group
                interactByLeaf: true,
                point: {events: {
                    mouseOver(this: any) {
                        highlightGroup(this.series, this.drillId);
                    },
                    mouseOut(this: any) {
                        highlightGroup(this.series, false);
                    },
                }},
                levelIsConstant: false,
                cursor: levelCount > 1 ? 'pointer' : undefined,
                levels,
            } as unknown as SeriesOptionsType],
        },
    };
};

export default renderTreemap;
